import { useTranslation } from "react-i18next";

import type {
  NewsSignalFinbertPoint,
  NewsSignalL6Point,
  PriceBar,
} from "./types";

export interface ChartLegendProps {
  tz: string;
  crosshairTime: string | null;
  price: Pick<PriceBar, "o" | "h" | "l" | "c" | "v"> | null;
  l6: NewsSignalL6Point | null;
  finbert: NewsSignalFinbertPoint | null;
  /** Tick-size-aware display precision (chartTheme.pricePrecisionFor). */
  pricePrecision?: number;
}

function fmtSigned(n: number): string {
  return (n >= 0 ? "+" : "") + n.toFixed(2);
}

export function ChartLegend({
  tz,
  crosshairTime,
  price,
  l6,
  finbert,
  pricePrecision = 2,
}: ChartLegendProps) {
  const { t } = useTranslation("dashboard");
  return (
    <div
      data-testid="chart-legend"
      className="text-xs text-muted-foreground flex flex-wrap gap-3 py-1"
    >
      <span>
        {crosshairTime
          ? new Intl.DateTimeFormat(undefined, {
              timeZone: tz,
              year: "numeric", month: "short", day: "2-digit",
              hour: "2-digit", minute: "2-digit", hour12: false,
            }).format(new Date(crosshairTime))
          : "—"}
      </span>
      <span>
        {t("chart.price")}: {price ? <strong>{price.c.toFixed(pricePrecision)}</strong> : "—"}
      </span>
      <span>
        L6: {l6 && l6.score !== null && l6.label ? (
          <>
            <strong>{l6.label}</strong>{" "}
            <strong>{fmtSigned(l6.score)}</strong>
            {l6.carry_forward ? ` ${t("chart.held")}` : ""}
            {l6.news_through ? (
              <span className="opacity-70">
                {" "}
                {t("chart.newsThrough", {
                  time: new Intl.DateTimeFormat(undefined, {
                    timeZone: tz, hour: "2-digit", minute: "2-digit",
                    hour12: false,
                  }).format(new Date(l6.news_through)),
                })}
              </span>
            ) : null}
          </>
        ) : "—"}
      </span>
      <span>
        FinBERT: {finbert && finbert.score !== null ? (
          <>
            <strong>{fmtSigned(finbert.score)}</strong>{" "}
            ({finbert.n_clusters})
          </>
        ) : "—"}
      </span>
    </div>
  );
}
